import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { FaTimes } from 'react-icons/fa';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import Modal from './Modal';
import { SIZES, LINE_TYPES } from '../firebase/schema';
import { getMockupUrl } from '../utils/fileHelpers';
import { fetchOrdersForCustomer } from '../utils/customerOrders';

const generateUniqueId = (() => {
  let counter = 0;
  return (prefix = '') => {
    counter += 1;
    return `${prefix}-${Math.random().toString(36).substr(2, 9)}-${counter}`;
  };
})();

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#98FB98',
    },
    text: {
      primary: '#fff',
      secondary: 'rgba(255, 255, 255, 0.7)',
    },
    background: {
      default: '#1C1C1C',
      paper: 'rgba(255, 255, 255, 0.05)',
    }
  },
  transitions: {
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195,
    },
    easing: {
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
    }
  }
});

const ModalContent = styled.div`
  padding: 20px;
  max-width: 1200px;
  width: 90vw;
  max-height: 80vh;
  overflow-y: auto;
  background: var(--al-bg);
  color: white;
  font-family: 'Montserrat', sans-serif;
`;

const Header = styled.div`
  margin-bottom: 24px;
`;

const Title = styled.h2`
  color: var(--al-primary);
  margin-bottom: 16px;
  font-size: 1.5rem;
  text-transform: uppercase;
`;

const CustomerSearchContainer = styled.div`
  position: relative;
  width: 100%;
`;

const StyledSearchInput = styled.input`
  width: 100%;
  padding: 12px;
  background: #363636;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0;
  color: white;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  font-size: 0.9rem;
  
  &:focus {
    outline: none;
    border-color: var(--al-primary);
  }
`;

const SearchResults = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #363636;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-top: none;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
`;

const SearchResultItem = styled.div`
  padding: 12px;
  cursor: pointer;
  text-transform: uppercase;
  
  &:hover {
    background: rgba(var(--al-primary-rgb), 0.1);
  }
`;

const DesignSearchContainer = styled.div`
  margin-top: 24px;
  margin-bottom: 16px;
`;

const DesignList = styled.div`
  margin-top: 24px;
`;

const DesignRow = styled.div`
  display: grid;
  grid-template-columns: 40px 1fr 2fr 2fr 1fr 40px;
  gap: 16px;
  align-items: center;
  padding: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  &:hover {
    background: rgba(var(--al-primary-rgb), 0.1);
  }
`;

const MockupContainer = styled.div`
  width: 40px;
  height: 40px;
  overflow: hidden;
  background: ${props => props.noImage ? '#FF7F7F' : '#363636'};
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${props => props.noImage ? 'default' : 'pointer'};
  transition: transform 0.2s;

  &:hover {
    transform: ${props => props.noImage ? 'none' : 'scale(1.05)'};
  }
`;

const MockupImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const LargeArtworkModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.95);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const LargeArtworkContent = styled.div`
  width: 95vw;
  height: 95vh;
  padding: 0;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const LargeArtworkImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: transparent;
  border: none;
  color: white;
  cursor: pointer;
  font-size: 20px;
  
  &:hover {
    color: var(--al-primary);
  }
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
`;

const CreateButton = styled.button`
  background: var(--al-primary);
  color: black;
  border: none;
  padding: 12px 24px;
  margin-top: 24px;
  width: 100%;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  font-weight: 600;
  cursor: pointer;
  
  &:hover {
    background: #7FFF7F;
  }
  
  &:disabled {
    background: #666;
    cursor: not-allowed;
  }
`;

const ReorderModal = ({ isOpen, onClose, onCreateReorder }) => {
  // Reset state when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setSelectedDesigns(new Set());
      setCustomerSearch('');
      setSelectedCustomer(null);
      setDesigns([]);
      setFilteredCustomers([]);
      setDesignSearch('');
    }
  }, [isOpen]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [designs, setDesigns] = useState([]);
  const [selectedDesigns, setSelectedDesigns] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [selectedArtwork, setSelectedArtwork] = useState(null);
  const [designSearch, setDesignSearch] = useState('');

  // Fetch customers on mount
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const q = query(collection(db, 'customers'), orderBy('client'));
        const querySnapshot = await getDocs(q);
        const customersData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setCustomers(customersData);
      } catch (error) {
        console.error('Error fetching customers:', error);
      }
    };

    fetchCustomers();
  }, []);

  const handleCustomerSearch = (e) => {
    const query = e.target.value;
    setCustomerSearch(query);
    
    if (query.trim() === '') {
      setFilteredCustomers([]);
      return;
    }
    
    const filtered = customers.filter(customer =>
      customer.client.toLowerCase().includes(query.toLowerCase())
    );
    setFilteredCustomers(filtered);
  };

  const handleCustomerSelect = async (customer) => {
    // Reset selections if it's a different customer
    if (selectedCustomer?.id !== customer.id) {
      setSelectedDesigns(new Set());
    }
    setSelectedCustomer(customer);
    setCustomerSearch(customer.client);
    setFilteredCustomers([]);
    
    if (customer) {
      setLoading(true);
      try {
        const sortedOrders = await fetchOrdersForCustomer(db, customer, {
          allCustomers: customers,
          includeLines: true
        });

        const uniqueDesigns = new Map();

        sortedOrders.forEach((order) => {
          (order.lines || []).forEach((line) => {
            const designKey = JSON.stringify({
              designName: line.designName,
              garmentId: line.garmentId,
              garmentColor: line.garmentColor,
              frontInkColors: line.frontInkColors,
              backInkColors: line.backInkColors,
              otherInkColors: line.otherInkColors,
              designFileUrl: line.designFileUrl
            });

            if (!uniqueDesigns.has(designKey) ||
                uniqueDesigns.get(designKey).dueDate < line.dueDate) {
              uniqueDesigns.set(designKey, {
                id: line.id,
                ...line,
                lastOrderDate: line.dueDate?.toDate?.() || line.dueDate || order.dueDate || new Date()
              });
            }
          });
        });

        const designs = Array.from(uniqueDesigns.values())
          .sort((a, b) => b.lastOrderDate - a.lastOrderDate);

        setDesigns(designs);
      } catch (error) {
        console.error('Error fetching designs:', error);
      }
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && filteredCustomers.length === 1) {
      handleCustomerSelect(filteredCustomers[0]);
    }
    if (e.key === 'Escape') {
      setFilteredCustomers([]);
    }
  };

  const handleDesignToggle = (designId) => {
    const newSelected = new Set(selectedDesigns);
    if (newSelected.has(designId)) {
      newSelected.delete(designId);
    } else {
      newSelected.add(designId);
    }
    setSelectedDesigns(newSelected);
  };

  const handleCreateReorder = () => {
    const selectedDesignsList = designs.filter(design => 
      selectedDesigns.has(design.id)
    ).map(design => {
      console.log('Processing design for reorder:', design);
      const productionFiles = Array.isArray(design?.files?.production)
        ? design.files.production
            .filter((f) => f && f.url)
            .map((f) => ({
              name: f.name || '',
              url: f.url || '',
              type: f.type || 'unassigned',
              storagePath: f.storagePath || null,
              sentToPrintAt: f.sentToPrintAt || null,
              driveFileId: f.driveFileId || null,
              driveWebViewLink: f.driveWebViewLink || null,
              source: f.source || null
            }))
        : [];
      return {
        design: design.designName || '',
        style: design.garmentId || '',
        color: design.garmentColor || '',
        type: design.type || 'screen',
        designFileUrl: design.designFileUrl || '',  // Use designFileUrl since that's what we see in the data
        frontInkColors: design.frontInkColors || [],
        backInkColors: design.backInkColors || [],
        otherInkColors: design.otherInkColors || [],
        ...(design.pressSetup && { pressSetup: design.pressSetup }),
        sizeType: design.sizeType || 'ADULT',
        cogs: design.cogs || '',
        price: design.pricePerPiece || '',
        files: {
          mockup: design?.files?.mockup || (design.designFileUrl ? { url: design.designFileUrl, fileName: null } : null),
          production: productionFiles
        }
        // Not passing sizes to avoid copying quantities
      };
    });

    // Pass the selected designs and customer data back to parent
    onCreateReorder({
      designs: selectedDesignsList,
      customer: {
        customerName: selectedCustomer.client,
        customerId: selectedCustomer.id
      }
    });
    
    onClose();
  };

  return (
    <ThemeProvider theme={theme}>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalContent>
          <Header>
            <Title>Create Reorder</Title>
            <CustomerSearchContainer>
              <StyledSearchInput
                type="text"
                value={customerSearch}
                onChange={handleCustomerSearch}
                onKeyDown={handleKeyDown}
                placeholder="SEARCH CUSTOMERS..."
              />
              {filteredCustomers.length > 0 && (
                <SearchResults>
                  {filteredCustomers.map(customer => (
                    <SearchResultItem
                      key={customer.id}
                      onClick={() => handleCustomerSelect(customer)}
                    >
                      {customer.client}
                    </SearchResultItem>
                  ))}
                </SearchResults>
              )}
            </CustomerSearchContainer>
          </Header>

        {loading ? (
          <div>Loading designs...</div>
        ) : (
          <>
            {designs.length > 0 && (
              <DesignSearchContainer>
                <StyledSearchInput
                  type="text"
                  value={designSearch}
                  onChange={(e) => setDesignSearch(e.target.value)}
                  placeholder="SEARCH DESIGNS..."
                />
              </DesignSearchContainer>
            )}
            <DesignList>
              {designs
                .filter(design => {
                  if (!designSearch.trim()) return true;
                  const searchLower = designSearch.toLowerCase();
                  const designName = (design.designName || '').toLowerCase();
                  return designName.includes(searchLower);
                })
                .map(design => (
                <DesignRow key={design.id}>
                  <Checkbox
                    type="checkbox"
                    checked={selectedDesigns.has(design.id)}
                    onChange={() => handleDesignToggle(design.id)}
                  />
                  <MockupContainer 
                    noImage={!getMockupUrl(design)}
                    onClick={() => getMockupUrl(design) && setSelectedArtwork(getMockupUrl(design))}
                  >
                    {getMockupUrl(design) && (
                      <MockupImage 
                        src={getMockupUrl(design)} 
                        alt={design.designName}
                      />
                    )}
                  </MockupContainer>
                  <div>{design.designName}</div>
                  <div>{design.garmentId || design.garmentStyle} - {design.garmentColor}</div>
                  <div>{new Date(design.lastOrderDate).toLocaleDateString()}</div>
                </DesignRow>
              ))}
            </DesignList>

            {designs.length > 0 && (() => {
              const filteredDesigns = designs.filter(design => {
                if (!designSearch.trim()) return true;
                const searchLower = designSearch.toLowerCase();
                const designName = (design.designName || '').toLowerCase();
                return designName.includes(searchLower);
              });
              
              return (
                <>
                  {designSearch.trim() && (
                    <div style={{ 
                      marginBottom: '12px', 
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: '0.9em'
                    }}>
                      Showing {filteredDesigns.length} of {designs.length} designs
                    </div>
                  )}
                  <CreateButton
                    disabled={selectedDesigns.size === 0}
                    onClick={handleCreateReorder}
                  >
                    Create Reorder ({selectedDesigns.size} designs)
                  </CreateButton>
                </>
              );
            })()}
            {designs.length === 0 && !loading && selectedCustomer && (
              <div style={{ 
                padding: '40px', 
                textAlign: 'center', 
                color: 'rgba(255, 255, 255, 0.6)',
                marginTop: '24px'
              }}>
                No previous designs found for this customer.
              </div>
            )}
          </>
        )}
      </ModalContent>
    </Modal>

    {/* Large Artwork Modal */}
    {selectedArtwork && (
      <LargeArtworkModal onClick={() => setSelectedArtwork(null)}>
        <LargeArtworkContent onClick={e => e.stopPropagation()}>
          <CloseButton onClick={() => setSelectedArtwork(null)}>
            <FaTimes />
          </CloseButton>
          <LargeArtworkImage src={selectedArtwork} alt="Large artwork preview" />
        </LargeArtworkContent>
      </LargeArtworkModal>
    )}
    </ThemeProvider>
  );
};

export default ReorderModal;
